// Theme-adaptive styles for react-select
const getSelectStyles = (isDark: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    "&:hover": {
      borderColor: isDark ? "#2563eb" : "#2563eb",
    },
    backgroundColor: isDark ? "#1f2937" : "#fff",
    borderColor: state.isFocused
      ? isDark
        ? "#2563eb"
        : "#2563eb"
      : isDark
        ? "#374151"
        : "#d1d5db",
    boxShadow: state.isFocused
      ? isDark
        ? "0 0 0 1px #2563eb"
        : "0 0 0 1px #2563eb"
      : undefined,
    color: isDark ? "#fff" : "#111",
  }),
  input: (base: any) => ({
    ...base,
    color: isDark ? "#fff" : "#111",
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: isDark ? "#1f2937" : "#fff",
    color: isDark ? "#fff" : "#111",
    zIndex: 9999,
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? isDark
        ? "#2563eb"
        : "#2563eb"
      : state.isFocused
        ? isDark
          ? "#374151"
          : "#e5e7eb"
        : isDark
          ? "#1f2937"
          : "#fff",
    color: state.isSelected ? "#fff" : isDark ? "#fff" : "#111",
    cursor: "pointer",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: isDark ? "#9ca3af" : "#6b7280",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: isDark ? "#fff" : "#111",
  }),
});
import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import Select from "react-select";

import { menuService } from "@/services/MenuService";

interface OrderEditModalProps {
  drivers?: any[];
  menuItems?: any[];
  onClose: () => void;
  onSubmit: (data: any) => void;
  open: boolean;
  order: any;
}

const OrderEditModal: React.FC<OrderEditModalProps> = ({
  drivers: propsDrivers,
  menuItems: propsMenuItems,
  onClose,
  onSubmit,
  open,
  order,
}) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<any>({});
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [menuItemsState, setMenuItemsState] = useState<any[]>([]);
  const [menuItemsError, setMenuItemsError] = useState(false);
  // Use menuItems from props, order, or fetched state
  // Always use the full menu item list for selects and lookups
  const menuItems =
    Array.isArray(propsMenuItems) && propsMenuItems.length > 0
      ? propsMenuItems
      : menuItemsState;
  // Fetch menu items from backend if not provided
  useEffect(() => {
    if (
      menuItems.length === 0 &&
      !loadingMenuItems &&
      !menuItemsError &&
      open
    ) {
      setLoadingMenuItems(true);
      menuService
        .getMenuItems()
        .then((res: any) => {
          setMenuItemsState(res.data);
          setMenuItemsError(false);
        })
        .catch(() => {
          setMenuItemsError(true);
        })
        .finally(() => setLoadingMenuItems(false));
    }
  }, [open, menuItems.length, loadingMenuItems, menuItemsError]);
  const drivers =
    Array.isArray(propsDrivers) && propsDrivers.length > 0
      ? propsDrivers
      : order?.drivers || [];
  useEffect(() => {
    if (order) {
      // Map order.items to { itemId, quantity } using menuItems if possible
      const items = Array.isArray(order.items)
        ? order.items.map((item: any) => {
            // Try to find the menu item by id or item_id
            const menuItem = menuItems.find(
              (m: any) => m.id === item.item_id || m.id === item.id,
            );
            return {
              itemId: menuItem ? menuItem.id : item.item_id || item.id || "",
              quantity: item.quantity || 1,
            };
          })
        : [{ itemId: "", quantity: 1 }];
      // Always set tableNumber, even if 0 or empty string
      let tableNumber = "";
      if (
        typeof order.table_number !== "undefined" &&
        order.table_number !== null
      ) {
        tableNumber = String(order.table_number);
      } else if (
        typeof order.tableNumber !== "undefined" &&
        order.tableNumber !== null
      ) {
        tableNumber = String(order.tableNumber);
      }
      setForm({
        address: order.delivery_address || order.address || "",
        customerName: order.customer_name || order.customerName || "",
        customerPhone: order.phone_number || order.customerPhone || "",
        deliveryNotes: order.delivery_notes || order.deliveryNotes || "",
        driverId: order.driver_id || order.driverId || "",
        items,
        orderType: order.order_type
          ? order.order_type.toLowerCase()
          : "delivery",
        paymentMethod: order.payment_method || order.paymentMethod || "",
        promoCode: order.promo_code || order.promoCode || "",
        status: order.status || "pending",
        tableNumber,
      });
      setStep(1);
    }
  }, [order]);
  if (!open || !order) return null;
  const updateItem = (index: number, field: string, value: any) => {
    const updated = [...form.items];
    updated[index][field] = value;
    setForm({ ...form, items: updated });
  };
  const addItemRow = () => {
    setForm({ ...form, items: [...form.items, { itemId: "", quantity: 1 }] });
  };
  const removeItemRow = (i: number) => {
    setForm({
      ...form,
      items: form.items.filter((_: any, x: number) => x !== i),
    });
  };
  // Calculate totals
  const getSubtotal = () =>
    form.items.reduce((sum: number, row: any) => {
      const item = menuItems.find((m: any) => m.id === row.itemId);
      return sum + (item ? item.price * row.quantity : 0);
    }, 0);
  const VAT_RATE = 0.15;
  const DELIVERY_FEE = form.orderType === "delivery" ? 25 : 0;
  const getVAT = () => getSubtotal() * VAT_RATE;
  const getTotal = () => getSubtotal() + getVAT() + DELIVERY_FEE;
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm((f: any) => ({ ...f, [name]: value }));
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Map items to include all backend-required fields
    const itemsWithDetails = form.items
      .filter((row: any) => row.itemId)
      .map((row: any) => {
        const item = menuItems.find((m: any) => m.id === row.itemId);
        return {
          item_id: item ? item.id : row.itemId,
          name: item ? item.name : undefined,
          price: item ? item.price : undefined,
          quantity: row.quantity,
        };
      });

    // Calculate totals
    const subtotal = itemsWithDetails.reduce(
      (sum: number, item: any) => sum + (item.price || 0) * item.quantity,
      0,
    );
    const vat = subtotal * 0.15;
    const delivery_fee = form.orderType === "delivery" ? 25 : 0;
    const total = subtotal + vat + delivery_fee;

    // Map orderType to backend values
    const orderTypeMap: Record<string, string> = {
      delivery: "Delivery",
      "dine-in": "Dine-in",
      takeaway: "Takeaway",
    };

    const payload = {
      customer_name: form.customerName,
      delivery_address: form.address,
      delivery_fee,
      delivery_notes: form.deliveryNotes,
      driver_id: form.driverId,
      items: itemsWithDetails,
      order_type: orderTypeMap[form.orderType],
      payment_method: form.paymentMethod,
      phone_number: form.customerPhone,
      promo_code: form.promoCode,
      status: form.status,
      subtotal,
      table_number: form.tableNumber,
      total,
      vat,
    };
    console.log("[OrderEditModal] Submitting order payload:", payload);
    onSubmit(payload);
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black/60 p-4">
        <form
          className="relative w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-900"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <button
            aria-label="Close"
            className="absolute right-4 top-4 rounded-full bg-transparent p-2 text-gray-500 hover:text-gray-700 focus:outline-none dark:text-gray-400 dark:hover:text-gray-200"
            onClick={onClose}
            type="button"
          >
            <X className="h-6 w-6" />
          </button>
          <h2 className="mb-4 text-2xl font-bold dark:text-white">
            Edit Order
          </h2>
          <div className="mb-6 flex items-center justify-between">
            {[1, 2, 3].map((n) => (
              <div
                className={`mx-1 h-2 flex-1 rounded ${step >= n ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-700"}`}
                key={n}
              />
            ))}
          </div>
          {/* ===================== STEP 1 — CUSTOMER ===================== */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold dark:text-white">
                Customer Details
              </h3>
              <input
                className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                onChange={(e) =>
                  setForm({ ...form, customerName: e.target.value })
                }
                placeholder="Customer Name"
                required
                value={form.customerName ?? ""}
              />
              <input
                className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                onChange={(e) =>
                  setForm({ ...form, customerPhone: e.target.value })
                }
                placeholder="Phone Number"
                required
                value={form.customerPhone ?? ""}
              />
              <div>
                <label className="text-sm font-medium dark:text-gray-300">
                  Order Type
                </label>
                <select
                  className="mt-1 w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                  onChange={(e) =>
                    setForm({ ...form, orderType: e.target.value })
                  }
                  value={form.orderType}
                >
                  <option value="dine-in">Dine-In</option>
                  <option value="takeaway">Takeaway</option>
                  <option value="delivery">Delivery</option>
                </select>
              </div>
              {/* {form.orderType === "dine-in" && (
                <input
                  className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                  onChange={(e) =>
                    setForm({ ...form, tableNumber: e.target.value })
                  }
                  placeholder="Table Number"
                  value={form.tableNumber ?? ""}
                />
              )} */}
              <div className="flex justify-end">
                <button
                  className="rounded bg-blue-600 px-4 py-2 text-white"
                  onClick={() => setStep(2)}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
          {/* ===================== STEP 2 — ITEMS ===================== */}
          {step === 2 && (
            <div>
              <h3 className="mb-4 text-lg font-semibold dark:text-white">
                Order Items
              </h3>
              {form.items.map((row: any, index: number) => {
                const isDark =
                  typeof document !== "undefined" &&
                  (document.documentElement.classList.contains("dark") ||
                    document.body.classList.contains("dark"));
                // Find the current item object for pre-select
                const currentItem = menuItems.find(
                  (i: any) => i.id === row.itemId,
                );
                return (
                  <div className="mb-3 flex gap-2" key={index}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <Select
                        isClearable
                        isDisabled={loadingMenuItems}
                        onChange={(opt) =>
                          updateItem(index, "itemId", opt ? opt.value : "")
                        }
                        options={menuItems.map((i: any) => ({
                          label: `${i.name} — R${i.price}`,
                          value: i.id,
                        }))}
                        placeholder={
                          loadingMenuItems ? "Loading..." : "Choose Item"
                        }
                        styles={getSelectStyles(
                          typeof document !== "undefined" &&
                            (document.documentElement.classList.contains(
                              "dark",
                            ) ||
                              document.body.classList.contains("dark")),
                        )}
                        value={
                          currentItem
                            ? {
                                label: `${currentItem.name} — R${currentItem.price}`,
                                value: currentItem.id,
                              }
                            : null
                        }
                      />
                    </div>
                    <input
                      className="w-24 rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                      min={1}
                      onChange={(e) =>
                        updateItem(index, "quantity", Number(e.target.value))
                      }
                      type="number"
                      value={row.quantity}
                    />
                    {form.items.length > 1 && (
                      <button
                        className="rounded bg-red-500 px-3 py-2 text-white"
                        onClick={() => removeItemRow(index)}
                        type="button"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
              <button
                className="mt-2 rounded bg-gray-700 px-3 py-1 text-sm text-white"
                onClick={addItemRow}
                type="button"
              >
                + Add Item
              </button>
              <div className="mt-6 rounded bg-gray-100 p-4 dark:bg-gray-800">
                <p className="text-gray-700 dark:text-gray-300">
                  Subtotal: R{getSubtotal().toFixed(2)}
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  VAT (15%): R{getVAT().toFixed(2)}
                </p>
                {form.orderType === "delivery" && (
                  <p className="text-gray-700 dark:text-gray-300">
                    Delivery Fee: R{DELIVERY_FEE}
                  </p>
                )}
                <p className="mt-2 text-xl font-bold dark:text-white">
                  Total: R{getTotal().toFixed(2)}
                </p>
              </div>
              <div className="mt-6 flex justify-between">
                <button
                  className="rounded bg-gray-500 px-4 py-2 text-white"
                  onClick={() => setStep(1)}
                >
                  ← Back
                </button>
                <button
                  className="rounded bg-blue-600 px-4 py-2 text-white"
                  onClick={() => setStep(3)}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
          {/* ===================== STEP 3 — DELIVERY & PAYMENT ===================== */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="mb-2 text-lg font-semibold dark:text-white">
                Delivery & Payment
              </h3>
              {form.orderType === "delivery" && (
                <>
                  <input
                    className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                    onChange={(e) =>
                      setForm({ ...form, address: e.target.value })
                    }
                    placeholder="Delivery Address"
                    value={form.address ?? ""}
                  />
                  <textarea
                    className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                    onChange={(e) =>
                      setForm({ ...form, deliveryNotes: e.target.value })
                    }
                    placeholder="Delivery Notes (optional)"
                    rows={2}
                    value={form.deliveryNotes ?? ""}
                  />
                  <select
                    className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                    onChange={(e) =>
                      setForm({ ...form, driverId: e.target.value })
                    }
                    value={form.driverId}
                  >
                    <option value="">Assign Driver</option>
                    {drivers.map((d: any) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <select
                className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                onChange={(e) =>
                  setForm({ ...form, paymentMethod: e.target.value })
                }
                value={form.paymentMethod}
              >
                <option value="">Select Payment Method</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="eft">Bank Transfer</option>
              </select>
              <input
                className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                onChange={(e) =>
                  setForm({ ...form, promoCode: e.target.value })
                }
                placeholder="Promo Code"
                value={form.promoCode ?? ""}
              />
              <select
                className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                value={form.status}
              >
                <option value="pending">Pending</option>
                <option value="preparing">Preparing</option>
                <option value="ready">Ready for Pickup</option>
                <option value="delivering">Out for Delivery</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <div className="mt-6 flex justify-between">
                <button
                  className="rounded bg-gray-500 px-4 py-2 text-white"
                  onClick={() => setStep(2)}
                >
                  ← Back
                </button>
                <button
                  className="rounded bg-green-600 px-4 py-2 text-white"
                  type="submit"
                >
                  Save ✓
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default OrderEditModal;
